import { createSlice } from "@reduxjs/toolkit";
import {
  fetchProjectsData,
  createProject,
  fetchProjectById,
  fetchProjectTags,
} from "../Thunks/projectManagementThunk";
import { mapProjectsListResponse } from "../../Pages/Projects/projectMappers";
import { isSupersededRequestPayload } from "../helpers/asyncResource";

const emptyProjectDetail = () => ({
  loading: false,
  error: null,
  raw: null,
  requestId: null,
});

const emptyProjectTags = () => ({
  loading: false,
  error: null,
  raw: null,
  projectId: null,
});

const initialState = {
  data: null,
  loading: false,
  error: null,
  allListLoaded: false,
  allListData: null,
  projectDetail: emptyProjectDetail(),
  projectTags: emptyProjectTags(),
};

export const projectsSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    clearProjects: (state) => {
      state.data = null;
      state.error = null;
      state.allListLoaded = false;
      state.allListData = null;
      state.projectDetail = emptyProjectDetail();
      state.projectTags = emptyProjectTags();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjectsData.pending, (state, action) => {
        if (action.meta?.arg?.getAll === true) return;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectsData.fulfilled, (state, action) => {
        const mapped = mapProjectsListResponse(action.payload);
        if (action.meta?.arg?.getAll === true) {
          state.allListLoaded = true;
          state.allListData = mapped;
          return;
        }
        state.loading = false;
        state.error = null;
        state.data = mapped;
      })
      .addCase(fetchProjectsData.rejected, (state, action) => {
        if (isSupersededRequestPayload(action.payload)) return;
        if (action.meta?.arg?.getAll === true) return;
        state.loading = false;
        state.error = action.payload ?? action.error?.message ?? "Something went wrong";
      })
      .addCase(createProject.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(createProject.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchProjectById.pending, (state, action) => {
        state.projectDetail.loading = true;
        state.projectDetail.error = null;
        state.projectDetail.raw = null;
        state.projectDetail.requestId = action.meta.arg;
        state.projectTags = emptyProjectTags();
      })
      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.projectDetail.loading = false;
        state.projectDetail.error = null;
        state.projectDetail.raw = action.payload;
        state.projectDetail.requestId = action.meta.arg;
      })
      .addCase(fetchProjectById.rejected, (state, action) => {
        if (isSupersededRequestPayload(action.payload)) return;
        state.projectDetail.loading = false;
        state.projectDetail.error =
          action.payload ?? action.error?.message ?? "Failed to fetch project data";
        state.projectDetail.raw = null;
      })
      .addCase(fetchProjectTags.pending, (state, action) => {
        state.projectTags.loading = true;
        state.projectTags.error = null;
        state.projectTags.raw = null;
        state.projectTags.projectId = action.meta.arg;
      })
      .addCase(fetchProjectTags.fulfilled, (state, action) => {
        state.projectTags.loading = false;
        state.projectTags.error = null;
        state.projectTags.raw = action.payload;
        state.projectTags.projectId = action.meta.arg;
      })
      .addCase(fetchProjectTags.rejected, (state, action) => {
        if (isSupersededRequestPayload(action.payload)) return;
        state.projectTags.loading = false;
        state.projectTags.error =
          action.payload ?? action.error?.message ?? "Failed to fetch project tags";
        state.projectTags.raw = null;
      });
  },
});

export const { clearProjects } = projectsSlice.actions;
export const selectProjectsData = (state) => state.projects.data;
export const selectProjectsLoading = (state) => state.projects.loading;
export const selectProjectsError = (state) => state.projects.error;
/** Full list from GET all-projects API — for filter dropdowns; paginated `data` stays separate. */
export const selectProjectsAllListData = (state) => state.projects.allListData;
/**
 * Dropdowns / filters: `fetchProjectsData({ getAll: true })` only fills `allListData`;
 * paginated lists use `data`. Prefer full list when present.
 */
export const selectProjectsListForDropdown = (state) =>
  state.projects.allListData ?? state.projects.data;
export const selectProjectDetail = (state) => state.projects.projectDetail;
export const selectProjectDetailRaw = (state) => state.projects.projectDetail.raw;
export const selectProjectDetailLoading = (state) => state.projects.projectDetail.loading;
export const selectProjectDetailError = (state) => state.projects.projectDetail.error;
export const selectProjectTagsState = (state) => state.projects.projectTags;

export default projectsSlice.reducer;
